import { Component, inject, OnInit, signal, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgxPaginationModule } from 'ngx-pagination';
import { finalize } from 'rxjs';

import { ProviderType } from 'src/app/enumeration/provider-type';
import { PageSize } from 'src/app/enumeration/pageSize';
import { BlockPhoneApiService } from 'src/app/service/block-phone/block-phone-api.service';
import { ThemeService } from 'src/app/service/theme/theme.service';

// Explicitly define a type contract to map dot notation data parameters cleanly
export interface BlockedPhoneRecord {
  srNo: number | string;
  paymentPhone: string;
  blockedDate?: string;
  reason?: string;
  paymentName?: string;
  billTopUPhone?: string;
  loginPhone?: string;
  isBlock?: string;
}

interface BlockPhoneListResponse {
  allTotal: number;
  kbzpayBlockPaymentRespLst?: BlockedPhoneRecord[];
  wavepayBlockPaymentRespLst?: BlockedPhoneRecord[];
  ayapayBlockPaymentRespLst?: BlockedPhoneRecord[];
  billTopUpBlockPhoneRespLst?: BlockedPhoneRecord[];
}

@Component({
  selector: 'app-block-phone',
  standalone: true,
  imports: [CommonModule, NgxPaginationModule, FormsModule],
  templateUrl: './block-phone.component.html',
  styleUrl: './block-phone.component.css',
})
export class BlockPhoneComponent implements OnInit {
  // Functional constructor-less dependency injections
  private readonly apiService = inject(BlockPhoneApiService);
  private readonly destroyRef = inject(DestroyRef);

  // Fully refactored component view data structures using modern signals
  readonly results = signal<BlockedPhoneRecord[]>([]);
  readonly allTotal = signal<number>(0);
  readonly page = signal<number>(1); // Optimized 1-based index template pagination mapping
  readonly pageSize = signal<number>(PageSize.BLOCKPHONE_LIST);

  // Operational Spinners Flags
  readonly loading = signal<boolean>(false);
  readonly isUnBlockBtnLoading = signal<boolean[]>([]);

  // Exposing Provider Enums directly to the template
  readonly providerTypes = ProviderType;

  // Active operational state variable fields
  type: ProviderType = ProviderType.KBZ;

  ngOnInit(): void {
    this.getAllList();
  }

  /**
   * Dispatches network lookup commands to stream active blocked profiles matrices
   */
  getAllList(): void {
    this.results.set([]);
    this.loading.set(true);

    const zeroBasedPage = this.page() - 1; // Sync 1-based page signal back down to server pages requirements

    this.apiService.getBlockPhoneLists(this.type, zeroBasedPage)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false)) // Unconditionally turns off active background processing spinners
      )
      .subscribe({
        next: (data: BlockPhoneListResponse) => {
          // Replaced slow, redundant switch statements with a clean, immutable object index lookup mapping
          const responseKeyMap: Record<ProviderType, BlockedPhoneRecord[] | undefined> = {
            [ProviderType.KBZ]: data.kbzpayBlockPaymentRespLst,
            [ProviderType.WAVE]: data.wavepayBlockPaymentRespLst,
            [ProviderType.AYA]: data.ayapayBlockPaymentRespLst,
            [ProviderType.Phone_Bill]: data.billTopUpBlockPhoneRespLst,
          };

          this.results.set(responseKeyMap[this.type] || []);
          this.allTotal.set(data?.allTotal || 0);
        },
        error: (err: any) => {
          console.error('Failed to stream blocked telecommunications data:', err);
          alert(err?.message || err || 'Network failure while processing blacklist records.');
        }
      });
  }

  /**
   * Intercepts standard radio input changes to switch provider view parameters context
   */
  onRadioChange(event: Event): void {
    const selectedRadio = event.target as HTMLInputElement;
    if (selectedRadio.checked) {
      this.type = selectedRadio.value as ProviderType;
      this.page.set(1); // Force view parameters index back to page 1 upon provider category change
      this.getAllList();
    }
  }

  /**
   * Orchestrates the security profile restoration workflows configuration parameters
   */
  unBlockPhone(data: BlockedPhoneRecord, index: number): void {
    if (!data?.paymentPhone) return;

    // Extracted redundant multi-switch statements into a flat direct variable method pass
    this.unBlockPhoneEnd(data.paymentPhone, index, this.type);
  }

  private unBlockPhoneEnd(phone: string, index: number, type: ProviderType): void {
    const reason = prompt('Please Enter Reason:');
    if (reason === null) return; // Immediate graceful termination if user hits cancel on reason prompt

    if (!reason.trim()) {
      alert('A valid reason profile must be supplied before unlocking account nodes.');
      return;
    }

    if (!confirm('Are you absolutely sure you want to lift this device restriction lock block?')) {
      return;
    }

    // Mutate the specific inline loading row position safely via reactive signals arrays updates
    this.isUnBlockBtnLoading.update(loaders => {
      const copy = [...loaders];
      copy[index] = true;
      return copy;
    });

    this.apiService.getunBlockPhoneService(phone, reason, type)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.isUnBlockBtnLoading.update(loaders => {
            const copy = [...loaders];
            copy[index] = false;
            return copy;
          });
        })
      )
      .subscribe({
        next: () => {
          alert('Security operational block restriction successfully removed.');
          this.getAllList(); // Re-sync active listing metrics data elements grid
        },
        error: (err: any) => {
          console.error('Account unblocking clearance exception intercepted:', err);
          alert(err?.message || err || 'Critical fault intercept while lifting profile locks.');
        }
      });
  }

  /**
   * Captures user pagination click inputs to smoothly shift viewing indexes
   */
  handlePageChange(event: number): void {
    if (!this.loading()) {
      this.page.set(event);
      this.getAllList();
    }
  }
}
